/**
 * CloudConfigOnboarding — first-visit modal that walks a brand-new user
 * through "what IAM scope do I need" + "how do I create the credentials"
 * for AWS, Azure and GCP. Each tab carries:
 *
 *   - the minimum read-only role / scope CloudGenie needs
 *   - 4-line copy-paste CLI snippet to provision the credentials
 *   - the exact fields they'll paste back into Cloud Config
 *
 * Dismissal is sticky per-user (localStorage key
 * `cg.onboarding.cloud-config.dismissed.<user_id>`) so they only see it once.
 * Re-openable any time via the "Setup guide" button in the Cloud Config
 * header.
 */
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Cloud, Copy, Check, ChevronRight, ShieldCheck, Sparkles, X,
  AlertTriangle, Terminal,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Dialog, DialogContent } from '../ui/dialog';

// ---------------------------------------------------------------------------
// Per-provider playbook content.
// ---------------------------------------------------------------------------
const PROVIDERS = [
  {
    id: 'aws',
    label: 'AWS',
    accent: 'amber',
    needed_scope: [
      { name: 'ReadOnlyAccess', why: 'List EC2 / RDS / S3 / EKS / etc.' },
      { name: 'job-function/Billing', why: 'Cost Explorer + Pricing API' },
      { name: 'CloudWatchLogsReadOnlyAccess', why: 'SRE log monitoring (optional)' },
    ],
    snippet: `# Run in your AWS CLI shell. Substitute <ACCOUNT_NICKNAME> if you like.
aws iam create-user --user-name cloudgenie-readonly
aws iam attach-user-policy --user-name cloudgenie-readonly \\
    --policy-arn arn:aws:iam::aws:policy/ReadOnlyAccess
aws iam attach-user-policy --user-name cloudgenie-readonly \\
    --policy-arn arn:aws:iam::aws:policy/job-function/Billing
aws iam create-access-key --user-name cloudgenie-readonly
# → Save the AccessKeyId + SecretAccessKey it prints.`,
    paste_back: [
      'Access Key ID (starts with AKIA…)',
      'Secret Access Key (40 random chars)',
      'Default Region (e.g. us-east-1)',
    ],
  },
  {
    id: 'azure',
    label: 'Azure',
    accent: 'cyan',
    needed_scope: [
      { name: 'Cost Management Reader', why: 'Cost / billing analytics' },
      { name: 'Reader', why: 'List VMs · App Service · AKS · Storage · …' },
      { name: 'Log Analytics Reader', why: 'Azure Monitor logs (optional)' },
    ],
    snippet: `# Run in Azure Cloud Shell or az-cli logged in to the right tenant.
SUB=$(az account show --query id -o tsv)
az ad sp create-for-rbac \\
    --name cloudgenie-readonly \\
    --role "Cost Management Reader" \\
    --scopes /subscriptions/$SUB
az role assignment create \\
    --assignee <appId-from-previous-output> \\
    --role Reader \\
    --scope /subscriptions/$SUB
# → Save the appId, password and tenant from the JSON output.`,
    paste_back: [
      'Tenant ID',
      'Client ID (the appId)',
      'Client Secret (the password)',
      'Subscription ID',
    ],
  },
  {
    id: 'gcp',
    label: 'GCP',
    accent: 'emerald',
    needed_scope: [
      { name: 'roles/billing.viewer', why: 'Cloud Billing reports' },
      { name: 'roles/viewer', why: 'List GCE · GKE · Cloud SQL · GCS · …' },
      { name: 'roles/monitoring.viewer', why: 'Cloud Monitoring (optional)' },
    ],
    snippet: `# Run in Cloud Shell or gcloud-cli logged into the target project.
PROJECT_ID=$(gcloud config get-value project)
gcloud iam service-accounts create cloudgenie-readonly \\
    --display-name "CloudGenie read-only"
gcloud projects add-iam-policy-binding $PROJECT_ID \\
    --member="serviceAccount:cloudgenie-readonly@$PROJECT_ID.iam.gserviceaccount.com" \\
    --role="roles/billing.viewer"
gcloud projects add-iam-policy-binding $PROJECT_ID \\
    --member="serviceAccount:cloudgenie-readonly@$PROJECT_ID.iam.gserviceaccount.com" \\
    --role="roles/viewer"
gcloud iam service-accounts keys create cg-key.json \\
    --iam-account=cloudgenie-readonly@$PROJECT_ID.iam.gserviceaccount.com
# → Upload cg-key.json in the Cloud Config form.`,
    paste_back: [
      'Project ID',
      'Service Account JSON key (uploaded as a file)',
    ],
  },
];

const ACCENT_BORDER = {
  amber: 'border-amber-500/40 text-amber-300 bg-amber-500/5',
  cyan: 'border-cyan-500/40 text-cyan-300 bg-cyan-500/5',
  emerald: 'border-emerald-500/40 text-emerald-300 bg-emerald-500/5',
};
const ACCENT_TEXT = {
  amber: 'text-amber-300',
  cyan: 'text-cyan-300',
  emerald: 'text-emerald-300',
};
const ACCENT_TRIGGER_ACTIVE = {
  amber: 'data-[state=active]:border-amber-500/60 data-[state=active]:text-amber-200',
  cyan: 'data-[state=active]:border-cyan-500/60 data-[state=active]:text-cyan-200',
  emerald: 'data-[state=active]:border-emerald-500/60 data-[state=active]:text-emerald-200',
};

const DISMISS_KEY_PREFIX = 'cg.onboarding.cloud-config.dismissed.';

/**
 * `shouldShowOnboarding(userId)` — true when the current user hasn't
 * dismissed the tour yet. Resolves to false on SSR / missing user.
 */
export function shouldShowOnboarding(userId) {
  if (typeof window === 'undefined' || !userId) return false;
  try {
    return window.localStorage.getItem(DISMISS_KEY_PREFIX + userId) !== '1';
  } catch (_) {
    return false;
  }
}

export function markOnboardingDismissed(userId) {
  if (typeof window === 'undefined' || !userId) return;
  try {
    window.localStorage.setItem(DISMISS_KEY_PREFIX + userId, '1');
  } catch (_) { /* ignore quota errors */ }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function CloudConfigOnboarding({ open, onClose }) {
  const { user } = useAuth();
  const [copiedId, setCopiedId] = useState(null);

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1800);
    } catch (_) { /* clipboard may be blocked in iframe */ }
  };

  const dismiss = () => {
    markOnboardingDismissed(user?.user_id);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent
        className="max-w-3xl p-0 overflow-hidden border-slate-800 bg-slate-950 max-h-[88vh] flex flex-col"
        data-testid="cloud-config-onboarding"
      >
        {/* Hero */}
        <div className="relative px-7 pt-7 pb-5 border-b border-slate-800 bg-gradient-to-br from-cyan-500/10 via-slate-950 to-violet-500/10 shrink-0">
          <button
            onClick={dismiss}
            data-testid="onboarding-close"
            className="absolute top-4 right-4 h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-white hover:bg-slate-900"
            aria-label="Close onboarding"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/40 bg-cyan-500/5 px-3 py-1 text-xs text-cyan-300 mb-3">
            <Sparkles className="w-3 h-3" /> First-time setup · ~2 minutes
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-white">
            Connect your first cloud
          </h2>
          <p className="mt-2 text-sm text-slate-400 max-w-xl leading-relaxed">
            CloudGenie reads from your cloud — never writes. Pick AWS, Azure or
            GCP below; we'll show you the exact <strong>read-only IAM role</strong>
            we need and the <strong>4-line CLI</strong> that provisions it.
          </p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="aws" className="flex-1 overflow-y-auto">
          <TabsList className="bg-transparent border-b border-slate-800 px-7 pt-3 gap-1 rounded-none w-full justify-start sticky top-0 z-10 bg-slate-950">
            {PROVIDERS.map((p) => (
              <TabsTrigger
                key={p.id}
                value={p.id}
                data-testid={`onboarding-tab-${p.id}`}
                className={`gap-1.5 rounded-t-md border border-transparent border-b-0 px-4 py-2 text-slate-400 hover:text-slate-200 ${ACCENT_TRIGGER_ACTIVE[p.accent]}`}
              >
                <Cloud className="w-3.5 h-3.5" />
                {p.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {PROVIDERS.map((p) => (
            <TabsContent key={p.id} value={p.id} className="px-7 py-6 space-y-5 mt-0">
              {/* IAM scope explanation */}
              <section data-testid={`onboarding-${p.id}-scope`}>
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className={`w-4 h-4 ${ACCENT_TEXT[p.accent]}`} />
                  <h3 className="text-sm font-semibold text-white">
                    What scope CloudGenie asks for
                  </h3>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed mb-3">
                  Strictly read-only. Each role below maps to one capability — pick the
                  minimum your team is comfortable with.
                </p>
                <ul className="space-y-2">
                  {p.needed_scope.map((s) => (
                    <li
                      key={s.name}
                      className={`flex items-start gap-3 rounded-md border ${ACCENT_BORDER[p.accent]} px-3.5 py-2.5`}
                    >
                      <code className={`text-xs font-mono mt-0.5 shrink-0 ${ACCENT_TEXT[p.accent]}`}>
                        {s.name}
                      </code>
                      <span className="text-xs text-slate-400">— {s.why}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* CLI snippet */}
              <section data-testid={`onboarding-${p.id}-snippet`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Terminal className={`w-4 h-4 ${ACCENT_TEXT[p.accent]}`} />
                    <h3 className="text-sm font-semibold text-white">
                      Run this in your {p.label} CLI
                    </h3>
                  </div>
                  <button
                    onClick={() => copy(p.snippet, p.id)}
                    data-testid={`onboarding-${p.id}-copy`}
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-700 hover:bg-slate-900 text-slate-300"
                  >
                    {copiedId === p.id ? (
                      <>
                        <Check className="w-3 h-3 text-emerald-400" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <pre className="text-xs bg-slate-900/80 border border-slate-800 rounded-md p-4 overflow-x-auto leading-relaxed font-mono">
                  <code className="text-slate-200 whitespace-pre">{p.snippet}</code>
                </pre>
              </section>

              {/* Paste-back fields */}
              <section>
                <div className="flex items-center gap-2 mb-2">
                  <ChevronRight className={`w-4 h-4 ${ACCENT_TEXT[p.accent]}`} />
                  <h3 className="text-sm font-semibold text-white">
                    Paste these back into Cloud Config
                  </h3>
                </div>
                <ul className="space-y-1.5 text-xs text-slate-400">
                  {p.paste_back.map((field) => (
                    <li key={field} className="flex items-start gap-2">
                      <span className="text-slate-500 mt-0.5">·</span>
                      <span>{field}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Cost-safety footnote */}
              <section
                className="rounded-md border border-slate-800 bg-slate-900/50 px-4 py-3 flex gap-2.5"
                data-testid={`onboarding-${p.id}-cost-note`}
              >
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                <div className="text-xs text-slate-400 leading-relaxed">
                  <strong className="text-slate-200">No surprise bills.</strong>{' '}
                  CloudGenie's polling calls (Cost Explorer / Cost Management /
                  Cloud Billing) typically rack up <strong>under $1/mo</strong>{' '}
                  per cloud. We cache 60 min per (org × sku × region) so
                  re-renders are free.
                </div>
              </section>
            </TabsContent>
          ))}
        </Tabs>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-slate-800 bg-slate-950 flex items-center justify-between gap-3 shrink-0">
          <a
            href="/docs/integrations"
            target="_blank"
            rel="noreferrer"
            className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1"
            data-testid="onboarding-full-docs-link"
          >
            Full setup guide
            <ChevronRight className="w-3 h-3" />
          </a>
          <Button
            onClick={dismiss}
            data-testid="onboarding-dismiss"
            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"
          >
            Got it — let's connect
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
